import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  IInvitationsRepository,
  TenantInvitation,
  InvitationStatus,
  InvitationPreview,
  InvitationPreviewStatus,
  CreateInvitationInput,
  InvitationLanguage,
  TenantMember,
} from '@erp/data-access/interfaces';

/** Row shape returned by RPC list_tenant_invitations_with_relations. */
interface InvitationListRow {
  id: string;
  tenant_id: string;
  email: string;
  role_id: string;
  role_slug: string;
  role_name: string;
  invited_by: string;
  invited_by_email: string | null;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  accepted_by: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  created_at: string;
}

/** Row shape returned by the create_tenant_invitation RPC (the raw table). */
interface InvitationRpcRow {
  id: string;
  tenant_id: string;
  email: string;
  role_id: string;
  invited_by: string;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  accepted_by: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  created_at: string;
}

interface AcceptedMemberRow {
  user_id: string;
  tenant_id: string;
  role_id: string;
  role: string;
  created_at: string;
}

interface PreviewJson {
  status: InvitationPreviewStatus;
  tenantName?: string;
  roleName?: string;
  inviterEmail?: string;
  invitedEmail?: string;
  expiresAt?: string;
}

/** Wraps a PostgrestError (or any RPC failure shape) into a real Error.
 *  Supabase returns plain objects with a `message` property, but they don't
 *  inherit from Error — so consumer code doing `e instanceof Error` would
 *  fall through. Always returning an Error means the typed error code we
 *  RAISE EXCEPTION'd in SQL flows cleanly to the dialog's catch block. */
function rpcError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    const wrapped = new Error(e.message ?? 'rpc_failed');
    if (e.code) (wrapped as Error & { code?: string }).code = e.code;
    if (e.details) (wrapped as Error & { details?: string }).details = e.details;
    if (e.hint) (wrapped as Error & { hint?: string }).hint = e.hint;
    return wrapped;
  }
  return new Error(String(err));
}

/** Invitations on master DB. All writes go through SECURITY DEFINER RPCs
 *  for ACL enforcement. Email sending is handled by the Resend Edge Function
 *  (supabase/functions/send-invitation-email/) — the adapter just invokes it. */
export class SupabaseInvitationsRepository implements IInvitationsRepository {
  constructor(private supabase: SupabaseClient) {}

  async list(): Promise<TenantInvitation[]> {
    const { data, error } = await this.supabase.rpc(
      'list_tenant_invitations_with_relations',
      {},
    );
    if (error) throw rpcError(error);
    return ((data ?? []) as InvitationListRow[]).map(mapInvitationListRow);
  }

  async getPreview(token: string): Promise<InvitationPreview> {
    const { data, error } = await this.supabase.rpc('get_invitation_preview', {
      p_token: token,
    });
    if (error) throw rpcError(error);
    return (data as PreviewJson) ?? { status: 'not_found' };
  }

  async create(input: CreateInvitationInput): Promise<TenantInvitation> {
    const { data, error } = await this.supabase.rpc(
      'create_tenant_invitation',
      { p_email: input.email, p_role_id: input.roleId },
    );
    if (error) throw rpcError(error);
    // The RPC returns the raw row (no role join). Re-fetch via list to get
    // the joined role/inviter — the just-created row will be the newest.
    const created = data as InvitationRpcRow;
    const all = await this.list();
    const enriched = all.find((i) => i.id === created.id);
    if (enriched) return enriched;
    // Defensive fallback: shape from the raw row without joins.
    return mapInvitationRpcRow(created, '', '', '');
  }

  async sendEmail(
    invitationId: string,
    language: InvitationLanguage,
  ): Promise<void> {
    const { error } = await this.supabase.functions.invoke(
      'send-invitation-email',
      { body: { invitation_id: invitationId, language } },
    );
    if (error) {
      // The function returns JSON like { error: "code" } — surface that code.
      // FunctionsHttpError exposes the upstream payload via .context.json().
      let msg = 'send_invitation_email_failed';
      try {
        const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
        if (ctx?.json) {
          const body = await ctx.json();
          if (body?.error) msg = body.error;
        } else if (error && typeof error === 'object' && 'message' in error) {
          msg = String((error as { message: string }).message);
        }
      } catch {
        /* keep default */
      }
      throw new Error(msg);
    }
  }

  async createAndSend(
    input: CreateInvitationInput,
    language: InvitationLanguage,
  ): Promise<TenantInvitation> {
    const inv = await this.create(input);
    try {
      await this.sendEmail(inv.id, language);
    } catch (e) {
      // Re-throw but preserve the invitation in case the caller wants to
      // surface the link manually. We do NOT revoke — that would force
      // the admin to start over for a transient SMTP blip.
      const cause = e instanceof Error ? e.message : 'unknown';
      throw new Error(`invitation_created_but_email_failed:${cause}`);
    }
    return inv;
  }

  async revoke(invitationId: string): Promise<void> {
    const { error } = await this.supabase.rpc('revoke_tenant_invitation', {
      p_invitation_id: invitationId,
    });
    if (error) throw rpcError(error);
  }

  /** Re-invite UX: revoke the old invitation, create a fresh one for the
   *  same (email, role), send the email, return the new row. The old token
   *  is permanently dead — old links never accept. */
  async resend(
    invitationId: string,
    language: InvitationLanguage,
  ): Promise<TenantInvitation> {
    const all = await this.list();
    const old = all.find((i) => i.id === invitationId);
    if (!old) throw new Error('invitation_not_found');
    // Already accepted? Resending makes no sense.
    if (old.status === 'accepted') throw new Error('invitation_already_accepted');

    if (old.status !== 'revoked') {
      await this.revoke(invitationId);
    }
    return this.createAndSend(
      { email: old.email, roleId: old.roleId },
      language,
    );
  }

  async accept(token: string): Promise<TenantMember> {
    const { data, error } = await this.supabase.rpc(
      'accept_tenant_invitation',
      { p_token: token },
    );
    if (error) throw rpcError(error);
    const row = data as AcceptedMemberRow;
    // The RPC returns the raw tenant_members row. Look up the role for
    // display via the master roles table (RLS now lets us read this tenant's
    // roles since we just inserted a member row).
    const { data: role } = await this.supabase
      .from('roles')
      .select('slug, name, is_system')
      .eq('id', row.role_id)
      .maybeSingle();
    type SbAny = {
      auth: {
        getUser: () => Promise<{
          data: { user: { id: string; email: string | null } | null };
        }>;
      };
    };
    const { data: u } = await (this.supabase as unknown as SbAny).auth.getUser();
    const callerEmail = u?.user?.email ?? '';

    return {
      userId: row.user_id,
      tenantId: row.tenant_id,
      email: callerEmail,
      roleId: row.role_id,
      roleSlug: (role?.slug as string | undefined) ?? row.role,
      roleName: (role?.name as string | undefined) ?? row.role,
      isOwner: ((role?.slug as string | undefined) ?? row.role) === 'owner',
      isSystemRole: (role?.is_system as boolean | undefined) ?? true,
      createdAt: row.created_at,
    };
  }
}

function statusOf(row: {
  accepted_at: string | null;
  revoked_at: string | null;
  expires_at: string;
}): InvitationStatus {
  if (row.accepted_at) return 'accepted';
  if (row.revoked_at) return 'revoked';
  if (new Date(row.expires_at).getTime() <= Date.now()) return 'expired';
  return 'pending';
}

function mapInvitationListRow(r: InvitationListRow): TenantInvitation {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    email: r.email,
    roleId: r.role_id,
    roleSlug: r.role_slug,
    roleName: r.role_name,
    invitedBy: r.invited_by,
    invitedByEmail: r.invited_by_email,
    token: r.token,
    expiresAt: r.expires_at,
    acceptedAt: r.accepted_at,
    acceptedBy: r.accepted_by,
    revokedAt: r.revoked_at,
    revokedBy: r.revoked_by,
    createdAt: r.created_at,
    status: statusOf(r),
  };
}

function mapInvitationRpcRow(
  r: InvitationRpcRow,
  roleSlug: string,
  roleName: string,
  invitedByEmail: string,
): TenantInvitation {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    email: r.email,
    roleId: r.role_id,
    roleSlug,
    roleName,
    invitedBy: r.invited_by,
    invitedByEmail,
    token: r.token,
    expiresAt: r.expires_at,
    acceptedAt: r.accepted_at,
    acceptedBy: r.accepted_by,
    revokedAt: r.revoked_at,
    revokedBy: r.revoked_by,
    createdAt: r.created_at,
    status: statusOf(r),
  };
}
